import React from 'react';
import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { resolveReportDateRange } from '../../lib/regions/au';
import { buildReportDisplayTitle } from '../../utils/report-title';
import { useExportPreferences, useGenerateReport } from '../../hooks/useReportGeneration';
import { useReportingUserContext } from '../../hooks/useReportingUserContext';
import type { ReportFilters, ReportGenerationInput, ReportType } from '../../types/report.types';
import {
  ReportWizardStepBasic,
  buildBasicDefaults,
  type BasicStepState,
} from './ReportWizardStepBasic';
import { ReportWizardStepFilters } from './ReportWizardStepFilters';
import {
  ReportWizardStepBusinessClaims,
  emptyBusinessClaimsStepState,
  type BusinessClaimsStepState,
} from './ReportWizardStepBusinessClaims';
import { ReportWizardStepGenerate } from './ReportWizardStepGenerate';
import { useApplyVendorBusinessUse } from '../../hooks/useBusinessClaimsPrep';
import { buildVendorApplyRows } from '../../services/business-claims-wizard-utils';
import { downloadGeneratedReport } from '../../utils/report-download';

interface Props {
  isOpen: boolean;
  reportType: ReportType;
  reportTitle: string;
  initialFilters?: ReportFilters;
  initialStep?: number;
  onClose: () => void;
}

function wizardSteps(reportType: ReportType): string[] {
  if (reportType === 'business_expense') {
    return ['Basic Settings', 'Business claims', 'Generate Report'];
  }
  return ['Basic Settings', 'Data Filters', 'Generate Report'];
}

export function ReportWizardModal({
  isOpen,
  reportType,
  reportTitle,
  initialFilters,
  initialStep = 1,
  onClose,
}: Props): React.ReactElement | null {
  const { data: prefs } = useExportPreferences();
  const { data: reportingContext } = useReportingUserContext();
  const generate = useGenerateReport();
  const applyVendorClaims = useApplyVendorBusinessUse();
  const isBusinessExpense = reportType === 'business_expense';
  const steps = wizardSteps(reportType);

  const fyStartMonth = reportingContext?.financialYearStartMonth;
  const periodOptions = fyStartMonth
    ? { financialYearStartMonth: fyStartMonth }
    : undefined;
  const modalRef = useRef<HTMLDivElement>(null);

  const [step, setStep] = useState(initialStep);
  const [basic, setBasic] = useState<BasicStepState>(() => buildBasicDefaults(reportTitle));
  const [filters, setFilters] = useState<ReportFilters>(initialFilters ?? {});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [genPhase, setGenPhase] = useState<'summary' | 'generating' | 'success' | 'error'>('summary');
  const [result, setResult] = useState<Awaited<ReturnType<typeof generate.mutateAsync>>['data'] | null>(null);
  const [businessClaims, setBusinessClaims] = useState<BusinessClaimsStepState>(
    emptyBusinessClaimsStepState
  );
  const [claimsApplySummary, setClaimsApplySummary] = useState<{
    updated: number;
    skipped?: number;
  } | null>(null);
  const [stepBusy, setStepBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep(initialStep);
      const period = resolveReportDateRange(
        prefs?.defaultDatePreset ?? 'this_month',
        prefs?.customStartDate ?? undefined,
        prefs?.customEndDate ?? undefined,
        new Date(),
        periodOptions
      );
      const defaultTitle = buildReportDisplayTitle(reportType, period);
      setBasic(buildBasicDefaults(defaultTitle, prefs, reportType));
      setFilters(initialFilters ?? {});
      setValidationError(null);
      setGenPhase('summary');
      setResult(null);
      setBusinessClaims(emptyBusinessClaimsStepState());
      setClaimsApplySummary(null);
    }
  }, [isOpen, reportTitle, reportType, initialStep, initialFilters, prefs, fyStartMonth]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBasicChange = (patch: Partial<BasicStepState>) => {
    setBasic((prev) => {
      const next = { ...prev, ...patch };
      if (patch.format === 'pdf') {
        next.includeCharts = false;
      } else if (patch.format !== undefined) {
        if (!prev.includeCharts) next.includeCharts = true;
      }
      const dateChanged =
        patch.dateRangePreset !== undefined ||
        patch.customStartDate !== undefined ||
        patch.customEndDate !== undefined;
      if (dateChanged) {
        try {
          const period = resolveReportDateRange(
            next.dateRangePreset,
            next.customStartDate || undefined,
            next.customEndDate || undefined,
            new Date(),
            periodOptions
          );
          next.title = buildReportDisplayTitle(reportType, period);
        } catch {
          /* keep user-edited title */
        }
        if (reportType === 'business_expense') {
          setBusinessClaims(emptyBusinessClaimsStepState());
          setClaimsApplySummary(null);
        }
      }
      return next;
    });
  };

  const resolvedPeriod = (() => {
    try {
      return resolveReportDateRange(
        basic.dateRangePreset,
        basic.customStartDate || undefined,
        basic.customEndDate || undefined,
        new Date(),
        periodOptions
      );
    } catch {
      return null;
    }
  })();

  const goToNextStep = async () => {
    if (step === 1 && !validateBasic()) return;

    if (step === 2 && isBusinessExpense) {
      const applyRows = buildVendorApplyRows(businessClaims.rows);
      setValidationError(null);
      if (applyRows.length > 0) {
        setStepBusy(true);
        try {
          const applyResult = await applyVendorClaims.mutateAsync({
            rows: applyRows,
            overwriteExisting: businessClaims.overwriteExisting,
          });
          setClaimsApplySummary({
            updated: applyResult.updated,
            skipped: applyResult.skipped,
          });
        } catch (e) {
          setValidationError(
            e instanceof Error ? e.message : 'Failed to save business-use settings'
          );
          setStepBusy(false);
          return;
        }
        setStepBusy(false);
      } else {
        setClaimsApplySummary(null);
      }
    }

    setStep((s) => s + 1);
  };

  const validateBasic = (): boolean => {
    if (!basic.title.trim()) {
      setValidationError('Report title is required');
      return false;
    }
    if (basic.dateRangePreset === 'custom') {
      if (!basic.customStartDate || !basic.customEndDate) {
        setValidationError('Custom date range requires start and end dates');
        return false;
      }
      if (basic.customEndDate < basic.customStartDate) {
        setValidationError('End date must be on or after start date');
        return false;
      }
    }
    setValidationError(null);
    return true;
  };

  const handleGenerate = async () => {
    setGenPhase('generating');
    const input: ReportGenerationInput = {
      title: basic.title.trim(),
      reportType,
      format: basic.format,
      dateRangePreset: basic.dateRangePreset,
      customStartDate: basic.customStartDate || undefined,
      customEndDate: basic.customEndDate || undefined,
      filters,
      includeCharts: basic.includeCharts,
      includeRawData: basic.includeRawData,
    };
    try {
      const res = await generate.mutateAsync(input);
      setResult(res.data);
      if (res.data?.status === 'succeeded') {
        setGenPhase('success');
      } else {
        setGenPhase('error');
      }
    } catch (e) {
      setGenPhase('error');
      setResult({
        id: '',
        status: 'failed',
        errorMessage: e instanceof Error ? e.message : 'Generation failed',
      });
    }
  };

  let dateRangeLabel: string = basic.dateRangePreset;
  try {
    dateRangeLabel = resolveReportDateRange(
      basic.dateRangePreset,
      basic.customStartDate || undefined,
      basic.customEndDate || undefined,
      new Date(),
      periodOptions
    ).label;
  } catch {
    /* keep preset id */
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-wizard-title"
    >
      <div
        ref={modalRef}
        className={`flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl bg-white shadow-xl ${
          isBusinessExpense && step === 2 ? 'max-w-4xl' : 'max-w-2xl'
        }`}
      >
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 id="report-wizard-title" className="text-xl font-semibold text-gray-900">
                Generate Report
              </h2>
              <p className="mt-1 text-sm text-gray-600">{reportTitle}</p>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-6 flex items-center gap-2">
            {steps.map((label, i) => {
              const n = i + 1;
              return (
                <div key={label} className="flex flex-1 items-center gap-2">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                      step >= n ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {n}
                  </div>
                  <span className="hidden text-xs text-gray-600 sm:inline">{label}</span>
                  {i < steps.length - 1 && (
                    <div className={`h-0.5 flex-1 ${step > n ? 'bg-blue-600' : 'bg-gray-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <ReportWizardStepBasic
              state={basic}
              onChange={handleBasicChange}
              validationError={validationError}
              reportType={reportType}
            />
          )}
          {step === 2 && isBusinessExpense && resolvedPeriod && (
            <ReportWizardStepBusinessClaims
              dateFrom={resolvedPeriod.startDate}
              dateTo={resolvedPeriod.endDate}
              dateRangeLabel={resolvedPeriod.label}
              state={businessClaims}
              onChange={setBusinessClaims}
            />
          )}
          {step === 2 && !isBusinessExpense && (
            <ReportWizardStepFilters filters={filters} onChange={setFilters} />
          )}
          {step === 2 && isBusinessExpense && !resolvedPeriod && (
            <p className="text-sm text-red-600">Fix the date range in step 1 to continue.</p>
          )}
          {validationError && step === 2 && isBusinessExpense && (
            <p className="mt-3 text-sm text-red-600">{validationError}</p>
          )}
          {step === 3 && (
            <ReportWizardStepGenerate
              phase={genPhase}
              reportType={reportType}
              summary={{
                title: basic.title,
                dateRangeLabel,
                format: basic.format.toUpperCase(),
                categoryCount: filters.categoryIds?.length ?? 0,
                accountCount: filters.accountIds?.length ?? 0,
                businessClaimsUpdated: claimsApplySummary?.updated,
                businessClaimsSkipped: claimsApplySummary?.skipped,
              }}
              result={result ?? undefined}
              errorMessage={result?.status === 'failed' ? result.errorMessage : null}
              onDownload={() => {
                if (result?.status === 'succeeded' && result.downloadUrl) {
                  void downloadGeneratedReport(
                    result.downloadUrl,
                    result.title,
                    result.format
                  ).catch((e) => {
                    window.alert(e instanceof Error ? e.message : 'Download failed');
                  });
                }
              }}
              onRetry={() => {
                setGenPhase('summary');
                void handleGenerate();
              }}
            />
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 p-6">
          <button
            type="button"
            disabled={step === 1}
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            className="text-sm text-gray-600 disabled:opacity-40"
          >
            Previous
          </button>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="text-sm text-gray-600">
              Cancel
            </button>
            {step < 3 ? (
              <button
                type="button"
                disabled={stepBusy}
                onClick={() => void goToNextStep()}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {stepBusy ? 'Saving…' : 'Next'}
              </button>
            ) : genPhase === 'summary' ? (
              <button
                type="button"
                onClick={() => void handleGenerate()}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
              >
                Generate Report
              </button>
            ) : genPhase === 'success' ? (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white"
              >
                Close
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
